import {ellipsis} from 'polished';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {getHasAnalyticsResolutionsReport} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFeatureStatusesEnum, AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {ReportNavigationLink} from './reportNavigationLink';

/*
 * Constants.
 */

const legacyAnalyticsLinksBaseConfig: ReadonlyArray<LinkConfig> = [
  {
    type: AnalyticsReportsEnum.OVERVIEW,
    iconName: 'globeSmall',
    selectedIconName: 'globeFilledSmall',
    name: <Trans id="analytics-link-overview">Overview</Trans>,
  },
];

/*
 * Styles.
 */

export const StyledAnalyticsNavigationTitleDiv = styled.div`
  padding: 6px 8px 6px 14px;
  min-width: 0;

  line-height: normal;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};

  ${ellipsis()};

  color: ${(p) => p.theme.leftBar.header};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

interface ChevronStyleProps {
  $isExpanded: boolean;
}

const StyledChevronDiv = styled.div<ChevronStyleProps>`
  transform: translateY(0.5px);
  svg {
    transform: scaleY(${(p) => (p.$isExpanded ? '-1' : '1')});
  }
`;

/*
 * Props.
 */

type LinkConfig = {
  type: AnalyticsReportsEnum;
  iconName: IconName;
  selectedIconName: IconName;
  name: ReactNode;
};

interface NavigationLinkProps {
  panelId: AnalyticsPanelId;
  isDisabled: boolean;
  selectedReportType: AnalyticsReportsEnum;
}

/*
 * Component.
 */

export const LegacyReportNavigationLinks: FC<NavigationLinkProps> = ({
  panelId,
  isDisabled,
  selectedReportType,
}) => {
  const interactionId = 'analytics-navigation';
  const interactionProps = buildInteractionIdProps(interactionId);
  const hasAnalyticsResolutionsReport = useAppSelector(getHasAnalyticsResolutionsReport);
  const legacyAnalyticsLinksConfig = createLegacyAnalyticsLinksConfig(hasAnalyticsResolutionsReport);

  const theme = useUserTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  const isSelectedReportInSection = legacyAnalyticsLinksConfig.some(
    (config) => config.type === selectedReportType,
  );

  // If the currently selected report is in this section, expand it.
  const isActuallyExpanded = isExpanded || isSelectedReportInSection;

  return (
    <div style={{marginTop: '18px'}} {...interactionProps}>
      <StyledAnalyticsNavigationTitleDiv onClick={() => setIsExpanded(!isExpanded)}>
        <Trans id="analytics-legacy-navigation-title">Legacy</Trans>
        {!isSelectedReportInSection && (
          <StyledChevronDiv $isExpanded={isActuallyExpanded}>
            <Icon name="chevronExpandTiny" color={theme.leftBar.header} />
          </StyledChevronDiv>
        )}
      </StyledAnalyticsNavigationTitleDiv>
      {isActuallyExpanded &&
        legacyAnalyticsLinksConfig.map((link) => (
          <div key={link.type}>{renderNavigationLink(panelId, link, isDisabled)}</div>
        ))}
    </div>
  );
};

/*
 * Helpers.
 */

export function createLegacyAnalyticsLinksConfig(
  hasAnalyticsResolutionsReport: boolean,
): ReadonlyArray<LinkConfig> {
  const resolutionsReportLink: ReadonlyArray<LinkConfig> = hasAnalyticsResolutionsReport
    ? [
        {
          type: AnalyticsReportsEnum.CONVERSATIONS,
          iconName: 'channelChatOutlineSmall',
          selectedIconName: 'channelChatSmall',
          name: <Trans id="analytics-link-conversations">Conversations</Trans>,
        },
      ]
    : [];

  return [...legacyAnalyticsLinksBaseConfig, ...resolutionsReportLink];
}

function renderNavigationLink(panelId: AnalyticsPanelId, link: LinkConfig, isDisabled: boolean) {
  return (
    <ReportNavigationLink
      panelId={panelId}
      key={link.type}
      reportType={link.type}
      iconName={link.iconName}
      selectedIconName={link.selectedIconName}
      isDisabled={isDisabled}
      featureStatus={AnalyticsFeatureStatusesEnum.LEGACY}
    >
      {link.name}
    </ReportNavigationLink>
  );
}
